import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  createCommRoom,
  isCommAvailable,
  openCommSocket,
  type CommAnswer,
  type CommItem,
  type CommMember,
  type CommSocket,
  type CommStatus,
} from '../services/realtime/commClient';

/**
 * 리모컨 안의 '소통 프레젠테이션' 대상 (교사 쪽).
 * 방 정보는 localStorage 에 보관 → 새로고침해도 만료(6시간) 전까지 이어서 사용.
 * 소켓은 이 대상이 선택된 동안만 연결(주기 ping 없음, 끊기면 1회 재시도 후 '다시 연결').
 */
export type CommHostRoom = {
  roomId: string;
  expiresAt: number;
  createdAt: number;
  className: string;
  teacherName: string;
  entryCode: string;
  /** 지금 열린 수업 프레젠 키 (학생 화면 질문 필터) */
  pres?: string;
};
export const COMM_TARGET_PREFIX = 'comm:';
export const isCommTarget = (sid: string | null | undefined) => !!sid && sid.startsWith(COMM_TARGET_PREFIX);

const roomLs = (userId: string) => `cp-comm-host:v2:${userId}`;
const loadRoom = (userId: string | null): CommHostRoom | null => {
  if (!userId) return null;
  try {
    const r = JSON.parse(window.localStorage.getItem(roomLs(userId)) ?? 'null') as CommHostRoom | null;
    return r && r.roomId && r.expiresAt > Date.now() ? r : null;
  } catch {
    return null;
  }
};

export type CommHostHandlers = {
  onWelcome?: (items: CommItem[], answers: Record<string, CommAnswer[]>) => void;
  onItem?: (item: CommItem) => void;
  onAnswer?: (requestId: string, answer: CommAnswer) => void;
};

export function useCommHost(userId: string | null, canUse: boolean, active: boolean) {
  const available = isCommAvailable();
  const [room, setRoom] = useState<CommHostRoom | null>(() => loadRoom(userId));
  const [status, setStatus] = useState<CommStatus>('connecting');
  const [members, setMembers] = useState<CommMember[]>([]);
  const sockRef = useRef<CommSocket | null>(null);
  const handlersRef = useRef<CommHostHandlers>({});

  useEffect(() => setRoom(loadRoom(userId)), [userId]);
  /* 만료 시각이 지나면 정리 */
  useEffect(() => {
    if (!room) return undefined;
    const t = window.setTimeout(() => setRoom(loadRoom(userId)), Math.max(1000, room.expiresAt - Date.now() + 500));
    return () => window.clearTimeout(t);
  }, [room, userId]);

  const clearRoom = useCallback(() => {
    if (userId) window.localStorage.removeItem(roomLs(userId));
    setRoom(null);
    setMembers([]);
  }, [userId]);

  const roomId = room?.roomId ?? '';
  const connect = available && canUse && active && !!roomId;
  useEffect(() => {
    if (!connect) {
      setStatus('connecting');
      return undefined;
    }
    const sock = openCommSocket({
      roomId,
      role: 'teacher',
      getToken: async () => (await supabase.auth.getSession()).data.session?.access_token ?? '',
      onStatus: (s) => {
        setStatus(s);
        if (s === 'ended') clearRoom();
      },
      onFrame: (f) => {
        const h = handlersRef.current;
        switch (f.t) {
          case 'welcome':
            setMembers((f.members as CommMember[]) ?? []);
            h.onWelcome?.((f.items as CommItem[]) ?? [], (f.answers as Record<string, CommAnswer[]>) ?? {});
            return;
          case 'member': {
            const m = f.member as CommMember;
            setMembers((prev) => {
              const i = prev.findIndex((x) => x.memberId === m.memberId);
              if (i < 0) return [...prev, m];
              const next = prev.slice();
              next[i] = { ...next[i], ...m };
              return next;
            });
            return;
          }
          case 'item':
            h.onItem?.(f.item as CommItem);
            return;
          case 'answer':
            h.onAnswer?.(String(f.requestId), f.answer as CommAnswer);
            return;
          default:
        }
      },
    });
    sockRef.current = sock;
    const { data: sub } = supabase.auth.onAuthStateChange((ev, session) => {
      if (ev === 'TOKEN_REFRESHED' && session) sock.send({ t: 'auth', token: session.access_token });
    });
    return () => {
      sub.subscription.unsubscribe();
      sock.close();
      if (sockRef.current === sock) sockRef.current = null;
    };
  }, [connect, roomId, clearRoom]);

  const create = useCallback(
    async (input: { className: string; teacherName: string; entryCode: string }): Promise<string | null> => {
      if (!userId) return 'unauthorized';
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token ?? '';
      if (!token) return 'unauthorized';
      const r = await createCommRoom(token, input);
      if ('error' in r) return r.error;
      const next: CommHostRoom = { roomId: r.roomId, expiresAt: r.expiresAt, createdAt: Date.now(), ...input };
      window.localStorage.setItem(roomLs(userId), JSON.stringify(next));
      setMembers([]);
      setRoom(next);
      return null;
    },
    [userId]
  );

  /** 종료: 서버에서 방 삭제(연결돼 있지 않으면 잠깐 연결해서 종료) */
  const end = useCallback(async (): Promise<boolean> => {
    if (!room) return true;
    if (sockRef.current?.send({ t: 'end' })) return true;
    return new Promise<boolean>((resolve) => {
      let done = false;
      const s = openCommSocket({
        roomId: room.roomId,
        role: 'teacher',
        getToken: async () => (await supabase.auth.getSession()).data.session?.access_token ?? '',
        onStatus: (st) => {
          if (st === 'ended' && !done) {
            done = true;
            s.close();
            clearRoom();
            resolve(true);
          } else if ((st === 'disconnected' || st === 'error') && !done) {
            done = true;
            s.close();
            resolve(false);
          }
        },
        onFrame: (f) => {
          if (f.t === 'welcome') s.send({ t: 'end' });
        },
      });
      window.setTimeout(() => {
        if (done) return;
        done = true;
        s.close();
        resolve(false);
      }, 8000);
    });
  }, [room, clearRoom]);

  const send = useCallback((frame: Record<string, unknown>) => !!sockRef.current?.send(frame), []);
  const reconnect = useCallback(() => sockRef.current?.reconnect(), []);
  /** 방 제목(현재 수업 프레젠 이름)으로 바꾸기 — 같은 방/링크 유지, 접속 중 학생에게 즉시 반영 */
  const rename = useCallback(
    (className: string, pres?: string): boolean => {
      if (!room || !userId) return false;
      if (!sockRef.current?.send({ t: 'rename', className, ...(pres != null ? { pres } : {}) })) return false;
      const next: CommHostRoom = { ...room, className, ...(pres != null ? { pres } : {}) };
      try {
        window.localStorage.setItem(roomLs(userId), JSON.stringify(next));
      } catch {
        /* ignore */
      }
      setRoom(next);
      return true;
    },
    [room, userId]
  );

  return {
    available,
    canUse,
    room,
    targetId: room ? `${COMM_TARGET_PREFIX}${room.roomId}` : null,
    status,
    connected: connect && status === 'connected',
    members,
    handlersRef,
    create,
    end,
    send,
    reconnect,
    rename,
  };
}
